import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface Creative {
  id: number;
  name: string;
  size?: string;
  link?: string;
  has_tag?: boolean;
}

interface GalleryCreationModalProps {
  open: boolean;
  onClose: () => void;
  creatives: Creative[];
  campaignId: number | null;
  onCreateGallery: (params: {
    name: string;
    creative_ids: number[];
    campaign_id: number;
    device_type?: string;
    hosted_image_adms?: Record<number, string>;
  }) => Promise<{ share_url: string }>;
}

const MAX_CREATIVES = 50;

export default function GalleryCreationModal({
  open,
  onClose,
  creatives,
  campaignId,
  onCreateGallery,
}: GalleryCreationModalProps) {
  const [galleryName, setGalleryName] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleToggle = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === creatives.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(creatives.slice(0, MAX_CREATIVES).map((c) => c.id));
    }
  };

  const handleCreate = async () => {
    if (!campaignId) {
      setError('No campaign selected');
      return;
    }

    if (selectedIds.length === 0) {
      setError('Select at least one creative');
      return;
    }

    if (selectedIds.length > MAX_CREATIVES) {
      setError(`Maximum ${MAX_CREATIVES} creatives per gallery`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build adm for hosted image creatives (no tag) so they render in the gallery
      const hostedImageAdms: Record<number, string> = {};
      const selectedCreatives = creatives.filter((c) => selectedIds.includes(c.id));
      selectedCreatives.forEach((c) => {
        if (c.link && c.has_tag === false) {
          const [w, h] = (c.size || '300x250').split('x');
          const imgHtml = `<img src="${c.link}" width="${w}" height="${h}" style="display:block;max-width:100%;height:auto;" alt="${c.name}" />`;
          hostedImageAdms[c.id] = btoa(imgHtml);
        }
      });

      const result = await onCreateGallery({
        name: galleryName || `Gallery - ${new Date().toLocaleDateString()}`,
        creative_ids: selectedIds,
        campaign_id: campaignId,
        ...(Object.keys(hostedImageAdms).length > 0 ? { hosted_image_adms: hostedImageAdms } : {}),
      });
      setShareUrl(result.share_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create gallery');
    } finally {
      setIsLoading(false);
    }
  };

  const fullUrl = (() => {
    if (!shareUrl) return '';
    if (shareUrl.startsWith('/')) return `${window.location.origin}${shareUrl}`;
    return shareUrl;
  })();

  const handleCopy = async () => {
    if (fullUrl) {
      try {
        await navigator.clipboard.writeText(fullUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // clipboard write failed
      }
    }
  };

  const handleClose = () => {
    setGalleryName('');
    setSelectedIds([]);
    setError(null);
    setShareUrl(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Create Preview Gallery</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Success state */}
        {shareUrl ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Gallery created with {selectedIds.length} preview{selectedIds.length !== 1 ? 's' : ''}
            </Alert>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Share this gallery link:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                value={fullUrl}
                InputProps={{ readOnly: true }}
                size="small"
              />
              <IconButton
                onClick={handleCopy}
                color={copied ? 'success' : 'default'}
                title="Copy to clipboard"
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" sx={{ mt: 1, color: 'success.main', display: 'block' }}>
                Copied!
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {/* Gallery name */}
            <TextField
              fullWidth
              value={galleryName}
              onChange={(e) => setGalleryName(e.target.value)}
              placeholder="Gallery name (optional)"
              label="Gallery Name"
              size="small"
              disabled={isLoading}
              sx={{ mb: 2 }}
            />

            {/* Creative selection */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Select Creatives ({selectedIds.length}/{creatives.length})
              </Typography>
              <Button size="small" onClick={handleSelectAll} disabled={isLoading}>
                {selectedIds.length === creatives.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            <Box
              sx={{
                maxHeight: 300,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2,
              }}
            >
              {creatives.length === 0 ? (
                <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                  No creatives available for this campaign
                </Typography>
              ) : (
                creatives.map((creative) => (
                  <Box
                    key={creative.id}
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedIds.includes(creative.id)}
                          onChange={() => handleToggle(creative.id)}
                          disabled={isLoading}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{creative.name}</Typography>
                          {creative.size && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {creative.size}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ width: '100%', m: 0, overflow: 'hidden' }}
                    />
                  </Box>
                ))
              )}
            </Box>

            {/* Loading state */}
            {isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Generating {selectedIds.length} preview{selectedIds.length !== 1 ? 's' : ''}...
                </Typography>
              </Box>
            )}

            {/* Error state */}
            {error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {shareUrl ? (
          <>
            <Button onClick={handleClose} variant="outlined">
              Close
            </Button>
            <Button
              onClick={() => window.open(fullUrl, '_blank', 'noopener,noreferrer')}
              variant="contained"
            >
              Open Gallery
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={isLoading || selectedIds.length === 0 || !campaignId}
            >
              Create Gallery ({selectedIds.length})
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
